import React, { useState } from "react";
import Modal from "@material-ui/core/Modal";
import "./ReviewModal.css";

const REVIEW_DATA = {
  patient: {
    name: "Jane Smith",
    dob: "03/15/1982 (Age 43)",
    memberId: "BCT-8472951-01",
    insurance: "Blue Cross TN - PPO",
    groupNumber: "GRP-45821",
    coverageStatus: "Active - Verified",
  },
  medication: {
    drugName: "Wegovy 0.25 mg",
    dosingInstructions: "Subcutaneous injection once weekly. Titrate up to 2.4 mg over 16 weeks per manufacturer protocol.",
    quantityDuration: "4 pens per month (28-day supply) · 90-day initial approval requested",
    prescribingPhysician: "Dr. Sarah Chen, MD · Endocrinology / Internal Medicine",
    formInfo: "Form: Caremark Commercial · Medical necessity questions pre-filled from last visit note (AI-assisted). Review each answer before submit.",
  },
  questions: [
    {
      id: 1,
      question: "What is the diagnosis for which this drug is being prescribed?",
      answer: "Obesity (ICD-10: E66.01)",
      confidence: "high",
      expandable: true,
      detail: "The primary diagnosis for the medication Wegovy, as documented in the clinical notes and prescription, is obesity (ICD-10: E66.9). This is explicitly stated as the reason for prescribing weight management.",
      visitNote: {
        title: "Patient Visit Note",
        content: "Diagnosis:\n• Primary Diagnosis: Obesity (ICD-10: E66.9)\nPurpose: Prescribed for weight management in patients with obesity\n\nAssessment and Plan:\nThe patient is a 43-year old male with a BMI of 32 kg/m², indicating obesity. Previous medication, Metformin, and participation in a comprehensive weight management program over the last 6 months did not yield significant results. Due to the patient's BMI and history, it is recommended to start Wegovy for weight management.",
      },
    },
    {
      id: 2,
      question: "What are the patient's relevant clinical measurements?",
      answer: 'BMI: 34.2, Weight: 210 lbs, Height: 5\'6"',
      confidence: "high",
    },
    {
      id: 3,
      question: "Does the patient have any comorbidities related to this condition?",
      answer: "Type 2 diabetes, hypertension, sleep apnea",
      confidence: "high",
    },
    {
      id: 4,
      question: "What symptoms or clinical findings support this diagnosis?",
      answer: "Elevated A1C (8.2%), documented obesity for 3+ years",
      confidence: "high",
    },
    {
      id: 5,
      question: "What are the relevant lab results and dates?",
      answer: "A1C: 8.2% (01/15/2026), Lipid panel: LDL 165 (01/15/2026)",
      confidence: "high",
    },
    {
      id: 6,
      question: "Why is this specific drug medically necessary over alternatives?",
      answer: "Patient failed metformin and phentermine. Wegovy demonstrated superior weight loss outcomes in clinical trials for patients with BMI >30 and comorbid T2DM. No formulary alternative addresses both obesity and glycemic control.",
      confidence: "high",
      expandable: true,
      detail: "The patient has tried Metformin for weight management/obesity, as documented in both the medication history and patient questionnaire. There is no evidence of any other weight loss medications being tried. The patient has not previously taken a GLP-1 such as Wegovy.",
      visitNote: {
        title: "Patient Visit Note",
        content: "Previous attempts at weight loss with Metformin were not successful, showing no significant weight loss.",
      },
      recommendedAction: "Consider documenting specific weight loss goals and timeline for Wegovy therapy to strengthen PA justification.",
    },
    {
      id: 7,
      question: "Has the prescriber reviewed the patient's current medication list for interactions?",
      answer: "Yes",
      confidence: "high",
    },
    {
      id: 8,
      question: "Is the prescriber aware this drug requires prior authorization?",
      answer: "Yes",
      confidence: "high",
    },
    {
      id: 9,
      question: "Prescriber specialty and qualifications",
      answer: "Endocrinology / Internal Medicine",
      confidence: "high",
    },
  ],
  evidence: {
    visitNote: "Visit note — 01/28/2026 (BMI, failed metformin)",
    lab: "Lab — A1C 8.2%",
  },
};

const ReviewModal = ({ open, onClose, task, onSubmit }) => {
  const [expandedIds, setExpandedIds] = useState([]);

  const toggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = () => {
    if (typeof onSubmit === "function") {
      onSubmit();
    } else {
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <div className="review-modal">
        <div className="review-modal-header">
          <h2 className="review-modal-title">Review prior authorization</h2>
          <button className="review-close-btn" onClick={onClose}>&#x2715;</button>
        </div>

        <div className="review-modal-body">
          <div className="review-step-badge">STEP 2 OF 4 — REVIEW</div>

          <div className="review-alert">
            <span className="alert-icon">⚠</span>
            <span>
              <strong>Denial risk alert:</strong> Ensure exercise counseling is documented — payer may require lifestyle intervention notes for this indication.
            </span>
          </div>

          <div className="review-section">
            <div className="review-section-title">Patient Information</div>
            <div className="review-info-grid">
              <div className="review-info-item">
                <span className="info-label">Name</span>
                <span className="info-value">{REVIEW_DATA.patient.name}</span>
              </div>
              <div className="review-info-item">
                <span className="info-label">Date of Birth</span>
                <span className="info-value">{REVIEW_DATA.patient.dob}</span>
              </div>
              <div className="review-info-item">
                <span className="info-label">Member ID</span>
                <span className="info-value">{REVIEW_DATA.patient.memberId}</span>
              </div>
              <div className="review-info-item">
                <span className="info-label">Insurance</span>
                <span className="info-value">{REVIEW_DATA.patient.insurance}</span>
              </div>
              <div className="review-info-item">
                <span className="info-label">Group Number</span>
                <span className="info-value">{REVIEW_DATA.patient.groupNumber}</span>
              </div>
              <div className="review-info-item">
                <span className="info-label">Coverage Status</span>
                <span className="info-value" style={{ color: "#4CAF50" }}>{REVIEW_DATA.patient.coverageStatus}</span>
              </div>
            </div>
          </div>

          <div className="review-section">
            <div className="review-section-title">Medication Details</div>
            <div className="review-med-details">
              <div className="med-detail-row">
                <div className="info-label">Drug Name &amp; Strength</div>
                <div className="info-value med-drug-name">{REVIEW_DATA.medication.drugName}</div>
              </div>
              <div className="med-detail-row">
                <div className="info-label">Dosing Instructions</div>
                <div className="info-value">{REVIEW_DATA.medication.dosingInstructions}</div>
              </div>
              <div className="med-detail-row">
                <div className="info-label">Quantity &amp; Duration</div>
                <div className="info-value">{REVIEW_DATA.medication.quantityDuration}</div>
              </div>
              <div className="med-detail-row">
                <div className="info-label">Prescribing Physician</div>
                <div className="info-value">{REVIEW_DATA.medication.prescribingPhysician}</div>
              </div>
            </div>
            <p className="review-form-note">{REVIEW_DATA.medication.formInfo}</p>
          </div>

          <div className="review-section-questions">
            <div className="review-section-title">Clinical Questions</div>
            {REVIEW_DATA.questions.map((q) => (
              <div className="review-question-block" key={q.id}>
                <div className="review-question">
                  {q.question} <span className="required-star">*</span>
                </div>
                <div className="review-answer-box">{q.answer}</div>
                <div
                  className={`confidence-badge ${q.expandable ? "expandable" : ""}`}
                  onClick={() => q.expandable && toggleExpand(q.id)}
                >
                  <span className="confidence-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.5">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </span>
                  <span className="confidence-text">High Confidence Answer</span>
                  {q.expandable && (
                    <span className={`expand-chevron ${expandedIds.includes(q.id) ? "expanded" : ""}`}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden
                      >
                        <path
                          d="M6 15l6-6 6 6"
                          stroke="#2E7D32"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </div>

                {q.expandable && expandedIds.includes(q.id) && (
                  <div className="confidence-detail">
                    <p>{q.detail}</p>
                    {q.visitNote && (
                      <div className="evidence-card">
                        <div className="evidence-card-title">{q.visitNote.title}</div>
                        <div className="evidence-card-content">{q.visitNote.content}</div>
                      </div>
                    )}
                    {q.recommendedAction && (
                      <div className="evidence-card">
                        <div className="evidence-card-title">Recommended Action</div>
                        <div className="evidence-card-content">{q.recommendedAction}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="review-section">
            <div className="evidence-attached">
              <div className="evidence-attached-title">Evidence attached</div>
              <div className="evidence-item">{REVIEW_DATA.evidence.visitNote}</div>
              <div className="evidence-item">{REVIEW_DATA.evidence.lab}</div>
            </div>
          </div>

          <p className="review-footer-note">
            In production, this panel embeds or deep-links to the full review UI (submit to payer via ePA / phone / fax per system routing).
          </p>
        </div>

        {task && task.status === "READY FOR REVIEW" && (
          <div className="review-modal-footer">
            <button className="review-btn-draft" onClick={onClose}>Save draft</button>
            <button className="review-btn-submit" onClick={handleSubmit}>Submit to payer</button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ReviewModal;
